// Step 3 Component: Broadcast Type Selection
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  Radio,
  Switch,
  Grid,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { ReactComponent as Now } from "../../../assets/images/icons/campaign/broadcast/now.svg";
import { ReactComponent as Schedule } from "../../../assets/images/icons/campaign/broadcast/schedule.svg"; // Fixed import path

import { ColorsConstants } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import TimeSlotDropdown from "../CampaignOptions/SelectTimeSlot";
import WalletErrorDialog from "../../../common/wallet-balance-modal";
import AppDatePicker from "../../../common/app-date-picker";

const datePickerPopperSx = {
  /* Date selection */
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: ColorsConstants.primaryBlue100,
    color: "#fff",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
  /* TimePicker clock */
  "& .MuiClock-pin": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClockPointer-root": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClockPointer-thumb": {
    backgroundColor: ColorsConstants.primaryBlue100,
    borderColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClockNumber-root.Mui-selected": {
    backgroundColor: ColorsConstants.primaryBlue100,
    color: "#fff",
  },
  /* OK / Cancel buttons */
  "& .MuiPickersActionBar-root .MuiButton-root": {
    color: ColorsConstants.primaryBlue100,
    fontWeight: 600,
  },
};

const StepBroadcastSelection = ({
  broadcastType,
  handleBroadcastTypeChange,
  isMobile,
  isEditMode,
  selectedCampaign,
  isEnableCheckBoxVisible,
  enableTimeSlot,
  setEnableTimeSlot,
  timeSlot,
  setTimeSlot,
  checkBroadcastButtonDisabled,
  loading,
  handleSubmit,
  handleBack,
  walletErrorAcknowledged,
  setWalletErrorAcknowledged,
  walletValidation,
  campaignName,
  setCampaignName,
  campaignDescription,
  setCampaignDescription,
  scheduleDate,
  setScheduleDate,
  broadcastTime,
  setBroadcastTime,
  notifyBefore,
  setNotifyBefore,
  notifyStatus,
  setNotifyStatus,
  campaignsData,
  setLoading,
  formatTwoDecimals,
  pageParams,
  walletErrorDialog,
  setWalletErrorDialog,
  scheduleError,
}) => {
  // Local state for validation
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [summaryText, setSummaryText] = useState([]);

  const cardStyle = {
    p: 2,
    borderRadius: 2,
    mb: 2,
    cursor: isEditMode ? "not-allowed" : "pointer",
    opacity: isEditMode ? 0.6 : 1,
    backgroundColor: "#F6F6F6",
    transition: "all 0.2s ease",
    boxShadow: "none",
    border: "1px solid #E5E7EB",
    "&:hover": {
      borderColor: isEditMode
        ? "#E5E7EB"
        : ColorsConstants.primaryBackgroundColor,
    },
  };

  const selectedCardStyle = {
    ...cardStyle,
    borderColor: ColorsConstants.primaryBackgroundColor,
    backgroundColor: "#F0F7FF",
    borderWidth: "2px",
  };

  // Generate summary text for scheduled broadcasts
  useEffect(() => {
    if (broadcastType !== "one-time" || !scheduleDate || !broadcastTime) {
      setSummaryText([]);
      return;
    }

    // Base scheduled datetime
    const scheduledDate = new Date(scheduleDate);

    if (broadcastTime instanceof Date) {
      scheduledDate.setHours(
        broadcastTime.getHours(),
        broadcastTime.getMinutes(),
        0,
        0,
      );
    }

    const formattedDate = scheduledDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const formattedTime = scheduledDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    const summary = [
      `This broadcast will be sent on ${formattedDate} at ${formattedTime}.`,
    ];

    // Smart Retry logic
    if (enableTimeSlot && timeSlot) {
      const retryDate = new Date(scheduledDate);
      retryDate.setHours(retryDate.getHours() + Number(timeSlot));

      const retryDateFormatted = retryDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const retryTimeFormatted = retryDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      summary.push(
        `Smart Retry enabled — failed records will retry after ${timeSlot} hours on ${retryDateFormatted} at ${retryTimeFormatted}.`,
      );
    }

    setSummaryText(summary);
  }, [scheduleDate, broadcastTime, enableTimeSlot, timeSlot, broadcastType]);

  // Date validation
  const validateDate = (date) => {
    if (!date) return false;

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setDateError("Date cannot be in the past");
      return false;
    }

    setDateError("");
    return true;
  };

  // Time validation
  const validateTime = (time) => {
    if (!time || !scheduleDate) return false;

    setTimeError("");
    return true;
  };

  // Handle date change
  const handleDateChange = (newDate) => {
    setScheduleDate(newDate);
    validateDate(newDate);
    if (broadcastTime) {
      validateTime(broadcastTime);
    }
  };

  // Handle time change
  const handleTimeChange = (newTime) => {
    setBroadcastTime(newTime);
    validateTime(newTime);
  };

  // Disable past dates
  const shouldDisableDate = (date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  // Disable past times when today is selected
  const shouldDisableTime = (timeValue, clockType) => {
    if (!scheduleDate) return false;

    const selectedDate = new Date(scheduleDate);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() !== today.getTime()) {
      return false;
    }

    const now = new Date();
    const minimumTime = new Date();
    minimumTime.setHours(minimumTime.getHours() + 1, 0, 0, 0);

    if (clockType === "hours") {
      return timeValue < minimumTime.getHours();
    }

    if (clockType === "minutes") {
      if (
        broadcastTime &&
        broadcastTime.getHours() === minimumTime.getHours()
      ) {
        return timeValue < minimumTime.getMinutes();
      }
    }

    return false;
  };

  return (
    <Box>
      {/* Broadcast Type Paper */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #DFE5EC",
          p: 3,
          borderRadius: "12px",
          mb: 3,
        }}
      >
        <Typography sx={{ ...typography.h2, mb: 2 }}>
          Broadcast Type
        </Typography>
        <RadioGroup
          value={broadcastType}
          onChange={handleBroadcastTypeChange}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Option 1 - Now */}
          <Paper
            sx={{
              ...(broadcastType === "now" ? selectedCardStyle : cardStyle),
              flex: 1,
              minWidth: 200,
              maxWidth: "calc(50% - 8px)",
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
              p: 2,
            }}
            onClick={() =>
              !isEditMode &&
              handleBroadcastTypeChange({ target: { value: "now" } })
            }
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Now style={{ width: 24, height: 24, color: "#4B5563" }} />
              <Radio
                value="now"
                checked={broadcastType === "now"}
                disabled={isEditMode}
                sx={{
                  color: "#D1D5DB",
                  "&.Mui-checked": {
                    color: ColorsConstants.primaryBackgroundColor,
                  },
                  p: 0.5,
                }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 600,
                  color: "#505050",
                  mb: "6px",
                }}
              >
                Now
              </Typography>
              <Typography
                sx={{
                  ...typography.bodySmall,
                  color: "#6B7280",
                }}
              >
                Deliver immediately to all selected recipients
              </Typography>
            </Box>
          </Paper>

          {/* Option 2 - One-time */}
          <Paper
            sx={{
              ...(broadcastType === "one-time" ? selectedCardStyle : cardStyle),
              flex: 1,
              minWidth: 200,
              maxWidth: "calc(50% - 8px)",
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
              p: 2,
            }}
            onClick={() =>
              !isEditMode &&
              handleBroadcastTypeChange({ target: { value: "one-time" } })
            }
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Schedule style={{ width: 24, height: 24, color: "#4B5563" }} />
              <Radio
                value="one-time"
                checked={broadcastType === "one-time"}
                disabled={isEditMode}
                sx={{
                  color: "#D1D5DB",
                  "&.Mui-checked": {
                    color: ColorsConstants.primaryBackgroundColor,
                  },
                  p: 0.5,
                }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 600,
                  color: "#505050",
                  mb: "6px",
                }}
              >
                Schedule
              </Typography>
              <Typography
                sx={{
                  ...typography.bodySmall,
                  color: "#6B7280",
                }}
              >
                Choose a specific date and time for delivery
              </Typography>
            </Box>
          </Paper>
        </RadioGroup>

        {/* Conditional Content based on broadcastType */}
        {broadcastType === "now" && (
          <Box
            sx={{
              mt: 3,
              borderRadius: "12px",
              p: 2,
              background: "#ECF1FF",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <InfoOutlined
              sx={{
                color: "#6C7C93",
                fontSize: 20,
                mt: 0.2,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                lineHeight: 1.5,
              }}
            >
              This broadcast will be sent immediately to all selected
              recipients.
            </Typography>
          </Box>
        )}

        {broadcastType === "one-time" && (
          <>
            {/* Date and Time Inputs */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <AppDatePicker
                    format="dd/MM/yyyy"
                    value={scheduleDate}
                    onChange={handleDateChange}
                    minDate={new Date()}
                    disableFuture={false}
                    error={!!dateError}
                    helperText={dateError}
                    placeholder="Select date"
                    fullWidth
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Broadcast Time"
                    value={broadcastTime}
                    onChange={handleTimeChange}
                    slotProps={{
                      popper: { sx: datePickerPopperSx },
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!timeError,
                        helperText: timeError,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            height: 40,
                            ...typography.bodyLarge,
                            "& fieldset": {
                              borderColor: "#DFE5EC",
                            },
                            "&:hover fieldset": {
                              borderColor: "#DFE5EC",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor:
                                ColorsConstants.primaryBackgroundColor,
                            },
                          },
                          "& .MuiInputLabel-root": {
                            ...typography.bodyLarge,
                          },
                        },
                      },
                    }}
                    shouldDisableTime={shouldDisableTime}
                    ampm={false}
                    minutesStep={5}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            {/* Summary Section */}
            {summaryText.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: "12px",
                  background: "#ECF1FF",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <InfoOutlined
                  sx={{
                    color: "#6C7C93",
                    fontSize: 20,
                    mt: 0.2,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  {summaryText.map((text, index) => (
                    <Typography
                      key={index}
                      sx={{
                        ...typography.bodySmall,
                        color: "#1B2232",
                        lineHeight: 1.5,
                        mb: index < summaryText.length - 1 ? 1 : 0,
                      }}
                    >
                      {text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Smart Retry Paper - Common for both options */}
      {isEnableCheckBoxVisible && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #DFE5EC",
            p: 3,
            borderRadius: "12px",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 600,
                  color: "#1B2232",
                  mb: 0.5,
                }}
              >
                Enable Smart Retry
              </Typography>
              <Typography
                sx={{
                  ...typography.bodySmall,
                  color: "#6C7C93",
                }}
              >
                Automatically retries sending messages to users who were
                unreachable.
              </Typography>
            </Box>
            <Switch
              checked={enableTimeSlot}
              onChange={(e) => {
                const checked = e.target.checked;
                setEnableTimeSlot(checked);
                if (!checked) setTimeSlot("");
              }}
              sx={{
                width: 44,
                height: 24,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 0,
                  margin: "3px",
                  "&.Mui-checked": {
                    transform: "translateX(20px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: ColorsConstants.primaryBackgroundColor,
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 18,
                  height: 18,
                  boxShadow: "none",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 13,
                  backgroundColor: "#C8D0E0",
                  opacity: 1,
                },
              }}
            />
          </Box>

          {enableTimeSlot && (
            <Box sx={{ mt: 3, width: 351 }}>
              <TimeSlotDropdown
                value={timeSlot}
                onChange={setTimeSlot}
                error={!timeSlot ? "Please select a time slot" : null}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Schedule Error */}
      {scheduleError && (
        <Typography sx={{ ...typography.bodySmall, color: "red", mt: 2, mb: 2 }}>
          {scheduleError}
        </Typography>
      )}

      {/* Wallet Error Dialog */}
      <WalletErrorDialog
        open={walletErrorDialog.open}
        data={walletErrorDialog.data}
        onClose={() => setWalletErrorDialog({ open: false, data: null })}
        onRecharge={() => {
          setWalletErrorDialog({ open: false, data: null });
        }}
      />
    </Box>
  );
};

export default StepBroadcastSelection;